import React from "react";
import PropTypes from "prop-types";

import {
  VStack,
  HStack,
  Box,
  Link,
  Text,
  Modal,
  ModalOverlay,
  ModalFooter,
  ModalContent,
  ModalBody,
} from "@chakra-ui/react";

import { useStaticQuery, graphql } from "gatsby";
import MenuLink from "components/menu/MenuLink";
import useUI from "context/UIContext";
import useTranslation from "utils/translate";
import Navbar from "./Navbar";
import { useSSRBreakpointValue } from "@chakra-ui/gatsby-plugin/helper";

export const OverlayNav = () => {
  const t = useTranslation();
  const { navIsOpen, navOnClose } = useUI();
  const mainLinkFontSize = useSSRBreakpointValue(
    { base: "xl", lg: "2xl" },
    "xl",
  );

  const data = useStaticQuery(graphql`
    query overlayMenuQuery {
      sanityCompanyInformation {
        instagramHandle
        instagramRef
        mail
        telephone
      }
      sanityOverlayMenu {
        firstLevelOverlayMenu {
          title {
            en
            de
          }
          slug
          order
        }
        secondLevelOverlayMenu {
          title {
            en
            de
          }
          slug
          order
        }
      }
    }
  `);

  const menuData = data?.sanityOverlayMenu;
  const companyData = data?.sanityCompanyInformation;

  const handleNavTrigger = () => {
    if (navIsOpen) {
      navOnClose();
    }
  };

  return (
    <Modal isOpen={navIsOpen} onClose={navOnClose} size="full" bg="black">
      <ModalOverlay />
      <ModalContent
        px="var(--page-margin-x)"
        borderRadius="0"
        onClick={handleNavTrigger}
      >
        <ModalBody p="0" display="flex" flexDirection="column">
          <Navbar displayCartOnMobile="none" />
          <VStack
            spacing="0"
            alignItems="flex-start"
            justifyContent="center"
            flexGrow="1"
          >
            {menuData.firstLevelOverlayMenu.map((navEntry) => (
              <MenuLink
                key={navEntry.slug}
                slug={navEntry.slug}
                lineHeight="1em"
                fontSize={mainLinkFontSize}
              >
                {t(navEntry.title)}
              </MenuLink>
            ))}
            {/* Only show this on mobile (on desktop it is in the modal footer) */}
            <Box pt="3em" display={{ base: "block", lg: "none" }}>
              <GetInTouchTeaser companyData={companyData} />
              <Link isExternal href={companyData.instagramRef}>
                {companyData.instagramHandle}
              </Link>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter px="0">
          <HStack width="100%" spacing="0">
            <HStack
              pr="8ex"
              width="50%"
              display={{ base: "none", lg: "flex" }}
              justifyContent="flex-start"
              spacing="8ex"
            >
              <Box>
                <GetInTouchTeaser companyData={companyData} />
              </Box>
              <Box>
                <Link isExternal href={companyData.instagramRef}>
                  {companyData.instagramHandle}
                </Link>
              </Box>
            </HStack>
            <HStack
              width={{ base: "100%", md: "50%" }}
              spacing={["2ex", "5ex", "8ex"]}
              flexShrink="0"
              justifyContent={{
                base: "space-between",
                md: "flex-start",
                lg: "flex-end",
              }}
            >
              {menuData.secondLevelOverlayMenu.map((navEntry) => (
                <MenuLink key={navEntry.slug} slug={navEntry.slug}>
                  {t(navEntry.title)}
                </MenuLink>
              ))}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

OverlayNav.propTypes = {};

const GetInTouchTeaser = ({ companyData }) => (
  <Text>
    Get in touch via{" "}
    <Link href={"mailto:" + companyData.mail} textDecoration="underline">
      email
    </Link>{" "}
    or{" "}
    <Link
      display={{ base: "block", lg: "inline" }}
      href={"tel:" + companyData.telephone}
    >
      T {companyData.telephone}
    </Link>
  </Text>
);

GetInTouchTeaser.propTypes = {
  companyData: PropTypes.object,
};
